// apps/client/src/net/socket.ts — ticket → ws → hello → snapshot; backoff; seq gaps → resync; keepalive (§4.1, §4.7)
import { tuning } from '@whiskerton/content';
import { CLOSE, PROTOCOL_VERSION, type ApiErrorCode, type MoveState, type S2C, type Snapshot, type TownEvent, type TownId } from '@whiskerton/shared';
import type { Api } from '../api/endpoints';
import { HttpError } from '../api/http';
import { API_BASE, BUILD_ID } from '../boot/env';
import type { SocketState } from '../state/slices/world';
import { useGame } from '../state/store';
import { ActionClient } from './actions';
import { ClockSync } from './clock';
import { MoveThrottle, TokenBucket } from './limiter';

export type { SocketState };   // revision 10 item 3: declared once, in the world slice (M0-T03)

export function backoffDelay(attempt: number, rand: () => number): number {
  const list = tuning.net.reconnectBackoffMs, j = tuning.net.reconnectJitter;
  return Math.round(list[Math.min(attempt, list.length - 1)]! * (1 - j + rand() * 2 * j));
}
/** Calls `fn` when the app may have lost its link unnoticed: back in the foreground, or back online. Returns the unsubscribe. */
export type Lifecycle = (fn: () => void) => () => void;
const browserLifecycle: Lifecycle = (fn) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  const vis = () => { if (document.visibilityState === 'visible') fn(); };
  document.addEventListener('visibilitychange', vis);
  window.addEventListener('online', fn);
  return () => { document.removeEventListener('visibilitychange', vis); window.removeEventListener('online', fn); };
};
type Deps = { api: Pick<Api, 'ticket'>; onSnapshot(s: Snapshot): void; onMoves(m: Extract<S2C, { t: 'moves' }>): void; onCorrect(c: Extract<S2C, { t: 'correct' }>): void;
  onEvents(e: TownEvent[]): void; onState(s: SocketState): void; wsFactory?: (url: string) => WebSocket; lifecycle?: Lifecycle };

export class TownSocket {
  readonly actions: ActionClient;
  readonly clock: ClockSync;
  state: SocketState = 'idle';
  /** The close code that stopped the socket (4001 replaced, 4003 not a resident, …), for the Game's toast. */
  lastClose: number | null = null;
  /** The ticket error that stopped the socket (unauthorized, forbidden, town_archived, …). */
  lastHttp: ApiErrorCode | null = null;
  private ws: WebSocket | null = null;
  private seq = 0;
  /** A seq gap already asked for a snapshot: later events of the same gap don't ask again. */
  private resyncPending = false;
  private attempt = 0;
  private stopped = false;
  private moveN = 0;
  private hb: ReturnType<typeof setInterval> | null = null;
  private dead: ReturnType<typeof setTimeout> | null = null;
  /** The connect deadline (helloTimeoutMs): handshake, DO wake and snapshot must land within it. Cleared by the snapshot. */
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private resyncClock: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private unwatch: (() => void) | null = null;
  private readonly throttle = new MoveThrottle();
  private readonly moveCap = new TokenBucket(tuning.net.movesPerSec, tuning.net.movesPerSec, performance.now());
  private readonly townId: TownId;
  private readonly deps: Deps;
  constructor(townId: TownId, deps: Deps) {
    this.townId = townId; this.deps = deps;
    this.clock = new ClockSync((c) => this.raw({ t: 'ping', c }));
    this.actions = new ActionClient({
      send: (f) => this.raw(f), online: () => this.state === 'live', resync: () => this.raw({ t: 'resync' }),
      toast: (key, params) => useGame.getState().toast(key, params, 'warn'),
      onPriv: (patch) => this.deps.onEvents([{ k: 'you.patch', patch }]),
    });
  }
  get town(): TownId { return this.townId; }
  connect(): void {
    this.stopped = false;
    this.unwatch ??= (this.deps.lifecycle ?? browserLifecycle)(() => this.checkAlive());
    void this.open();
  }
  close(): void {
    this.stopped = true;
    this.unwatch?.(); this.unwatch = null;
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    const ws = this.ws;
    this.ws = null;
    this.stopHeartbeat(); this.clearConnect();
    this.actions.failAll();
    if (ws) { ws.onopen = null; ws.onmessage = null; ws.onclose = null; ws.close(CLOSE.normal, 'bye'); }
    if (this.state !== 'updateRequired') this.set('closed');
  }
  /** Rate-limited: 10 Hz while moving, an animation change at once, a 15/s hard cap (§4.7). */
  sendMove(s: MoveState): void {
    const now = performance.now();
    if (this.state !== 'live' || !this.throttle.shouldSend(now, s, (t) => this.moveCap.take(t))) return;
    this.raw({ t: 'mv', s, n: ++this.moveN });
  }
  /** After a correction or a space change, the next move goes out even if it looks unchanged. */
  resetMoves(): void { this.throttle.reset(); }
  /** Back in the foreground or online (§14): a phone's socket may have died while it slept without any close event. Send a keepalive
   *  and give it deadAfterMs to answer; a pending reconnect goes now instead of waiting out its backoff. */
  checkAlive(): void {
    if (this.stopped) return;
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; void this.open(); return; }
    if (this.ws?.readyState === 1) this.ws.send('hb');
    if (this.ws && !this.connectTimer) this.armDead();            // still connecting: the connect deadline already covers it
  }
  private set(s: SocketState): void { if (this.state !== s) { this.state = s; this.deps.onState(s); } }
  private raw(m: unknown): boolean {
    if (!this.ws || this.ws.readyState !== 1 /* OPEN */) return false;
    this.ws.send(JSON.stringify(m));
    return true;
  }
  private url(path: string): string {
    const base = API_BASE || (typeof location !== 'undefined' ? `${location.protocol}//${location.host}` : 'http://localhost');
    return base.replace(/^http/, 'ws') + path;
  }
  private async open(): Promise<void> {
    this.retryTimer = null;
    this.resyncPending = false;                                   // a new connection starts with its own snapshot
    this.set('ticket');
    let wsUrl: string;
    try { wsUrl = (await this.deps.api.ticket(this.townId)).wsUrl; }
    catch (e) {
      if (e instanceof HttpError && (e.status === 401 || e.status === 403 || e.status === 404 || e.status === 410)) {
        this.stopped = true; this.lastHttp = e.error; this.set('closed'); return;
      }
      return this.retry();
    }
    if (this.stopped) return;
    this.set('connecting');
    const ws = (this.deps.wsFactory ?? ((u) => new WebSocket(u)))(this.url(wsUrl));
    this.ws = ws;
    this.armConnect();                                            // a connect that never opens (or never snapshots) counts as dead too
    ws.onopen = () => { ws.send(JSON.stringify({ t: 'hello', v: PROTOCOL_VERSION, build: BUILD_ID })); this.startHeartbeat(); };
    ws.onmessage = (e) => this.onMessage(String(e.data));
    ws.onclose = (e) => { if (this.ws === ws) this.onClose(e.code); };
  }
  private onMessage(data: string): void {
    this.alive();
    if (data === 'hb') return;
    let m: S2C;
    try { m = JSON.parse(data) as S2C; } catch { return; }
    switch (m.t) {
      case 'snapshot':
        this.seq = m.seq; this.resyncPending = false; this.attempt = 0; this.throttle.reset(); this.clearConnect(); this.set('live');
        void this.clock.sync(); this.clock.seed(m.snap.serverNow);   // sync() first drops the old best sample, so the snapshot's time
                                                                          // (a dev-clock change pushes one) applies until the pongs refine it
        this.resyncClock ??= setInterval(() => void this.clock.sync(), tuning.net.clockResyncMs);
        this.deps.onSnapshot(m.snap);
        return;
      case 'ev':
        if (m.seq !== this.seq + 1) {                                            // a gap: ask once for a fresh snapshot
          if (!this.resyncPending) this.resyncPending = this.raw({ t: 'resync' });
          return;
        }
        this.seq = m.seq;
        this.deps.onEvents(m.e);
        return;
      case 'moves': this.deps.onMoves(m); return;
      case 'correct': this.throttle.reset(); this.deps.onCorrect(m); return;
      case 'ack': this.actions.onAck(m); return;
      case 'pong': this.clock.onPong(m.c, m.s); return;
      case 'error':
        if (m.code === 'update_required') { this.stopped = true; this.set('updateRequired'); }
        else if (m.code === 'replaced' || m.code === 'not_resident' || m.code === 'town_closed' || m.code === 'abuse') this.stopped = true;
        return;
    }
  }
  private onClose(code: number): void {
    this.stopHeartbeat(); this.clearConnect();
    this.actions.failAll();
    this.ws = null;
    if (this.state === 'updateRequired') return;
    if (code === CLOSE.updateRequired) { this.stopped = true; this.set('updateRequired'); return; }
    if (this.stopped || code === CLOSE.replaced || code === CLOSE.notResident || code === CLOSE.townClosed || code === CLOSE.abuse) {
      this.stopped = true; this.lastClose = code; this.set('closed'); return;
    }
    this.retry();
  }
  private retry(): void {
    if (this.stopped) return;
    this.set('backoff');
    this.retryTimer = setTimeout(() => { if (!this.stopped) void this.open(); }, backoffDelay(this.attempt++, Math.random));
  }
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.hb = setInterval(() => {
      if (this.ws?.readyState === 1) this.ws.send('hb');
      if (!this.connectTimer) this.armDead();                     // still connecting: the connect deadline already covers it
    }, tuning.net.heartbeatMs);
  }
  /** Once live, a heartbeat (or a foreground check) must be answered within deadAfterMs. */
  private armDead(): void { this.dead ??= setTimeout(() => this.onDead(), tuning.net.deadAfterMs); }
  /** A fresh connect gets the longer helloTimeoutMs: a slow phone network or a cold town may need more than deadAfterMs to snapshot. */
  private armConnect(): void {
    this.clearConnect();
    this.connectTimer = setTimeout(() => { this.connectTimer = null; this.onDead(); }, tuning.net.helloTimeoutMs);
  }
  private clearConnect(): void { if (this.connectTimer) { clearTimeout(this.connectTimer); this.connectTimer = null; } }
  /** Nothing came back in time. On a dead link the browser's close event may not fire until TCP gives up, so detach the socket and
   *  start the reconnect ourselves (the Reconnecting banner shows at once). */
  private onDead(): void {
    this.alive();                                                 // the connect deadline can fire while a dead timer is pending: clear it,
                                                                  // or it would later tear down the next connection
    const ws = this.ws;
    if (!ws) return;
    ws.onopen = null; ws.onmessage = null; ws.onclose = null;
    try { ws.close(CLOSE.normal, 'silent'); } catch { /* already gone */ }
    this.onClose(CLOSE.normal);
  }
  private alive(): void { if (this.dead) { clearTimeout(this.dead); this.dead = null; } }
  private stopHeartbeat(): void {
    if (this.hb) clearInterval(this.hb);
    if (this.dead) clearTimeout(this.dead);
    if (this.resyncClock) clearInterval(this.resyncClock);
    this.hb = null; this.dead = null; this.resyncClock = null;
  }
}
